import { captureError } from '@/config/sentry';
import { useAuthStore } from '@/stores/authStore';
import { isSupportedTimeZone } from '@/utils/timezone';

const reportedTimeZones = new Set<string>();

const getDeviceTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * `guardian-portal-api` already translates Radius' Windows zone ids to IANA and falls back when it
 * cannot, so an unusable value here means that translation was bypassed or is out of date. Falling
 * back to the guardian's own zone keeps such a center behaving exactly as it did before MYM-260,
 * rather than showing a plausible-looking time from the wrong coast, and the Sentry event names the
 * center so the data can be fixed at the source. Reported once per distinct value.
 */
const reportUnusableTimeZone = (timeZone: string, centerName?: string) => {
  if (reportedTimeZones.has(timeZone)) return;

  reportedTimeZones.add(timeZone);
  captureError(new Error('Center time zone is not a usable IANA zone'), { timeZone, centerName });
};

/**
 * The zone of the center the guardian belongs to. Its own module so that `useTimeFor` resolves the
 * center through a boundary a test can replace without having to mock the whole auth store.
 */
export const useCenterTimeZone = (): string => {
  const { userData } = useAuthStore();

  const centerTimeZone = userData?.center?.timezone;

  if (!centerTimeZone) return getDeviceTimeZone();

  if (!isSupportedTimeZone(centerTimeZone)) {
    reportUnusableTimeZone(centerTimeZone, userData?.center?.name);

    return getDeviceTimeZone();
  }

  return centerTimeZone;
};
